import debounce from "lodash/debounce";
import { useEffect, useLayoutEffect, useState } from "react";

export interface UseResizeObserverOptions {
  debounceMs?: number;
}

function getElementSize(element: HTMLElement | undefined) {
  if (!element) {
    return { width: 0, height: 0 };
  }

  return { width: element.clientWidth, height: element.clientHeight };
}

export function useResizeObserver(
  element: HTMLElement | undefined,
  options: UseResizeObserverOptions = {}
) {
  const { debounceMs = 300 } = options;

  const [size, setSize] = useState(() => getElementSize(element));

  // Synchronously read initial dimensions before paint to avoid layout flash
  useLayoutEffect(() => {
    setSize(getElementSize(element));
  }, [element]);

  useEffect(() => {
    if (!element) {
      return undefined;
    }

    const handleResize = debounce((entries: ResizeObserverEntry[]) => {
      if (!entries || entries.length === 0) {
        return;
      }

      // eslint-disable-next-line prefer-destructuring
      const entry = entries[0];
      // Use clientWidth/clientHeight (content + padding) to stay consistent
      // with the initial synchronous measurement in useLayoutEffect
      setSize({
        width: entry.target.clientWidth,
        height: entry.target.clientHeight,
      });
    }, debounceMs);

    const observer = new ResizeObserver(handleResize);
    observer.observe(element);

    return () => {
      handleResize.cancel();
      observer.disconnect();
    };
  }, [debounceMs, element]);

  return size;
}
